import { useEffect, useMemo, useState } from 'react';
import { Activity, Droplets, Layers, TrendingUp } from 'lucide-react';
import { Area, AreaChart, Bar, ComposedChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatUnits, parseUnits } from 'viem';
import { useAccount, useReadContract, useReadContracts, useWaitForTransactionReceipt, useWriteContract } from 'wagmi';
import { MARKET_PAIRS, type MarketPair } from '../data/mockMarket';
import { ERC20_ABI, PAIR_ABI, ROUTER_ABI } from '../web3/abis';
import { TARGET_CHAIN } from '../web3/config';
import { getDeployment, getPairAddress, getTokenAddress } from '../web3/contracts';
import { logTransaction } from '../web3/logTransaction';
import {
  usePairStats,
  type PairStatsData,
  type SubgraphPair,
  type SubgraphPairDayData,
} from '../web3/useSubgraph';
import TokenIcon from './TokenIcon';
import TrustBar from './TrustBar';

const DECIMALS = 18;
const DEADLINE_SECONDS = 20 * 60;
const SLIPPAGE_PERCENT = 0.5;
const LP_FEE_PCT = 0.3; // Uniswap-v2 style swap fee routed to LPs

function fmt(value: bigint | undefined, digits = 4) {
  if (value === undefined) return '-';
  return Number(formatUnits(value, DECIMALS)).toLocaleString('en-US', { maximumFractionDigits: digits });
}

function fmtUsd(value: number | undefined) {
  if (value === undefined) return '—';
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: value < 1 && value > 0 ? 4 : 2 })}`;
}

const toNum = (v: bigint) => Number(formatUnits(v, DECIMALS));

// Batch-reads reserves for every pool and derives a USDT-denominated price for
// each token from its X-USDT pool, plus each pool's TVL. One multicall for the
// whole page instead of per-row reads.
function usePoolStats() {
  const { chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const deployment = getDeployment(readChainId);

  const contracts = useMemo(
    () =>
      MARKET_PAIRS.flatMap((p) => {
        const addr = deployment?.pairs[p.id];
        return [
          { address: addr, abi: PAIR_ABI, functionName: 'getReserves', chainId: readChainId } as const,
          { address: addr, abi: PAIR_ABI, functionName: 'token0', chainId: readChainId } as const,
        ];
      }),
    [deployment, readChainId],
  );

  const { data } = useReadContracts({
    contracts,
    query: { enabled: Boolean(deployment), refetchInterval: 8000 },
  });

  return useMemo(() => {
    const priceOf = new Map<string, number>([['USDT', 1]]);
    const reserves = new Map<string, { rBase: number; rQuote: number }>();
    if (!data || !deployment) return { priceOf, tvlOf: () => undefined, reserves, ready: false };

    MARKET_PAIRS.forEach((p, i) => {
      const res = data[i * 2];
      const t0 = data[i * 2 + 1];
      if (res.status !== 'success' || t0.status !== 'success') return;
      const [r0, r1] = res.result as readonly [bigint, bigint, number];
      const baseAddr = deployment.tokens[p.base]?.toLowerCase();
      const baseIs0 = (t0.result as string).toLowerCase() === baseAddr;
      const rBase = toNum(baseIs0 ? r0 : r1);
      const rQuote = toNum(baseIs0 ? r1 : r0);
      reserves.set(p.id, { rBase, rQuote });
      // seed prices from any pool that has USDT on one side
      if (p.quote === 'USDT' && rBase > 0) priceOf.set(p.base, rQuote / rBase);
      if (p.base === 'USDT' && rQuote > 0) priceOf.set(p.quote, rBase / rQuote);
    });

    const tvlOf = (poolId: string): number | undefined => {
      const pair = MARKET_PAIRS.find((p) => p.id === poolId);
      const r = reserves.get(poolId);
      if (!pair || !r) return undefined;

      // A V2 pool is balanced at its own spot price, so a USDT pair's USD TVL
      // is exactly twice its on-chain USDT reserve.
      if (pair.quote === 'USDT') return r.rQuote * 2;
      if (pair.base === 'USDT') return r.rBase * 2;

      const pb = priceOf.get(pair.base);
      const pq = priceOf.get(pair.quote);
      if (pb === undefined && pq === undefined) return undefined;
      // value both sides; if one token has no direct USD price, mirror the other
      const baseVal = pb !== undefined ? r.rBase * pb : r.rQuote * (pq ?? 0);
      const quoteVal = pq !== undefined ? r.rQuote * pq : r.rBase * (pb ?? 0);
      return baseVal + quoteVal;
    };

    return { priceOf, tvlOf, reserves, ready: true };
  }, [data, deployment]);
}

type PoolStats = ReturnType<typeof usePoolStats>;

function findSubgraphPair(data: PairStatsData | undefined, pairAddress: string | undefined) {
  if (!data || !pairAddress) return undefined;
  const id = pairAddress.toLowerCase();
  const pair = data.pairs.find((candidate) => candidate.id.toLowerCase() === id);
  if (!pair) return undefined;
  const days = data.pairDayDatas.filter((day) => day.pair.id.toLowerCase() === id);
  return { pair, days };
}

function finiteNumber(value: string): number | undefined {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function formatPoolTvl(pool: MarketPair, stats: PoolStats) {
  const tvl = stats.tvlOf(pool.id);
  if (tvl !== undefined) return `${pool.base === 'USDT' || pool.quote === 'USDT' ? '' : '≈'}${fmtUsd(tvl)}`;

  const reserves = stats.reserves.get(pool.id);
  if (!reserves) return '—';
  return `≈${(reserves.rBase * 2).toLocaleString('en-US', { maximumFractionDigits: 2 })} ${pool.base}`;
}

function formatVolume24h(
  pool: MarketPair,
  pair: SubgraphPair | undefined,
  day: SubgraphPairDayData | undefined,
) {
  if (!pair || !day) return '—';
  if (pool.base === 'USDT' || pool.quote === 'USDT') return fmtUsd(finiteNumber(day.dailyVolumeUSD));

  const baseIsToken0 = pair.token0.symbol === pool.base;
  const volume = finiteNumber(baseIsToken0 ? day.dailyVolumeToken0 : day.dailyVolumeToken1);
  return volume === undefined
    ? '—'
    : `${volume.toLocaleString('en-US', { maximumFractionDigits: 2 })} ${pool.base}`;
}

function safeParse(value: string): bigint | null {
  if (!value || Number.isNaN(Number(value)) || Number(value) <= 0) return null;
  try {
    return parseUnits(value as `${number}`, DECIMALS);
  } catch {
    return null;
  }
}

export default function PoolsView() {
  const [selected, setSelected] = useState<MarketPair>(MARKET_PAIRS[0]);
  const [mode, setMode] = useState<'add' | 'remove'>('add');
  const stats = usePoolStats();
  const subgraph = usePairStats();

  const orderedPools = useMemo(() => {
    return [...MARKET_PAIRS].sort((a, b) => (stats.tvlOf(b.id) ?? 0) - (stats.tvlOf(a.id) ?? 0));
  }, [stats]);

  const totalTvl = useMemo(
    () => (stats.ready ? MARKET_PAIRS.reduce((sum, p) => sum + (stats.tvlOf(p.id) ?? 0), 0) : undefined),
    [stats],
  );

  return (
    <div className="flex-1 flex flex-col lg:flex-row min-h-0 overflow-y-auto">
      <div className="w-full lg:w-[28rem] border-r border-abyss-700 shrink-0">
        <div className="p-4 border-b border-abyss-700">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foam-100">Liquidity Pools</h2>
            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-tide-400 bg-tide-400/10 px-1.5 py-0.5 rounded">
              <span className="w-1.5 h-1.5 rounded-full bg-tide-400 animate-pulse" />
              Live
            </div>
          </div>
          <p className="text-xs text-foam-500 mt-1">유동성을 공급하고 모든 스왑의 0.3% 수수료를 받으세요.</p>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-xs text-foam-500">전체 TVL</span>
            <span className="text-lg font-semibold text-foam-100 tabular-nums">
              {totalTvl === undefined ? '—' : `≈${fmtUsd(totalTvl)}`}
            </span>
          </div>
        </div>
        <div className="divide-y divide-abyss-700">
          {orderedPools.map((pool) => (
            <PoolRow
              key={pool.id}
              pool={pool}
              stats={stats}
              subgraphData={subgraph.data}
              isSelected={pool.id === selected.id}
              onSelect={() => setSelected(pool)}
            />
          ))}
        </div>
      </div>

      <div className="flex-1 p-6 min-w-0 space-y-6">
        <PoolDetail pool={selected} stats={stats} subgraphData={subgraph.data} />

        <div className="max-w-xl">
          <div className="flex gap-1 mb-4 p-1 rounded-lg bg-abyss-850 border border-abyss-700 w-fit">
            {(['add', 'remove'] as const).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  mode === m ? 'bg-tide-500 text-abyss-950' : 'text-foam-300 hover:text-foam-100'
                }`}
              >
                {m === 'add' ? 'Add Liquidity' : 'Remove Liquidity'}
              </button>
            ))}
          </div>

          {mode === 'add' ? <AddLiquidity pool={selected} /> : <RemoveLiquidity pool={selected} />}
          <div className="mt-6">
            <TrustBar />
          </div>
        </div>
      </div>
    </div>
  );
}

function PoolSparkline({
  pool,
  pair,
  days,
  baseAddress,
}: {
  pool: MarketPair;
  pair: SubgraphPair;
  days: SubgraphPairDayData[];
  baseAddress: string;
}) {
  const data = useMemo(() => {
    const baseIsToken0 = pair.token0.id.toLowerCase() === baseAddress.toLowerCase();
    return [...days]
      .reverse()
      .map((day) => {
        const reserve0 = finiteNumber(day.reserve0);
        const reserve1 = finiteNumber(day.reserve1);
        if (reserve0 === undefined || reserve1 === undefined) return undefined;
        const reserveBase = baseIsToken0 ? reserve0 : reserve1;
        const reserveQuote = baseIsToken0 ? reserve1 : reserve0;
        if (reserveBase <= 0) return undefined;
        return { t: day.date * 1000, price: reserveQuote / reserveBase };
      })
      .filter((point): point is { t: number; price: number } => point !== undefined);
  }, [baseAddress, days, pair.token0.id]);

  const up = data.length > 1 && data[data.length - 1].price >= data[0].price;
  const color = up ? '#22d3a8' : '#f5484f';

  return (
    <div className="h-20">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id="poolSpark" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.3} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="t" hide />
          <YAxis domain={['dataMin', 'dataMax']} hide />
          <Tooltip
            contentStyle={{ background: '#0d1420', border: '1px solid #182437', borderRadius: 8, fontSize: 12 }}
            labelFormatter={(value) => new Date(Number(value)).toLocaleDateString('ko-KR')}
            formatter={(value) => [Number(value).toFixed(6), `${pool.base}/${pool.quote}`]}
          />
          <Area type="monotone" dataKey="price" stroke={color} strokeWidth={2} fill="url(#poolSpark)" isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function PoolLiquidityHistory({ days }: { days: SubgraphPairDayData[] }) {
  const data = useMemo(
    () =>
      days
        .slice(0, 90)
        .reverse()
        .map((day) => {
          const volumeUSD = finiteNumber(day.dailyVolumeUSD);
          const tvlUSD = finiteNumber(day.reserveUSD);
          if (volumeUSD === undefined && tvlUSD === undefined) return undefined;
          return { t: day.date * 1000, volumeUSD, tvlUSD };
        })
        .filter(
          (point): point is { t: number; volumeUSD: number | undefined; tvlUSD: number | undefined } =>
            point !== undefined,
        ),
    [days],
  );

  if (data.length === 0) {
    return <div className="h-36 flex items-center justify-center text-xs text-foam-600">서브그래프 데이터 없음</div>;
  }

  return (
    <div>
      <div className="flex items-center gap-4 text-[10px] text-foam-500 mb-2">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-sm bg-sky-500" />
          일일 거래량
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-0.5 bg-tide-400" />
          TVL
        </span>
      </div>
      <div className="h-36">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="poolTvlHistory" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#22d3a8" stopOpacity={0.25} />
                <stop offset="100%" stopColor="#22d3a8" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="t"
              axisLine={false}
              tickLine={false}
              minTickGap={36}
              tick={{ fill: '#62748a', fontSize: 10 }}
              tickFormatter={(value) => new Date(Number(value)).toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' })}
            />
            <YAxis yAxisId="volume" hide />
            <YAxis yAxisId="tvl" orientation="right" hide domain={['auto', 'auto']} />
            <Tooltip
              contentStyle={{ background: '#0d1420', border: '1px solid #182437', borderRadius: 8, fontSize: 12 }}
              labelFormatter={(value) => new Date(Number(value)).toLocaleDateString('ko-KR')}
              formatter={(value, name) => [fmtUsd(Number(value)), name === 'volumeUSD' ? '일일 거래량' : 'TVL']}
            />
            <Bar yAxisId="volume" dataKey="volumeUSD" fill="#38bdf8" fillOpacity={0.45} radius={[2, 2, 0, 0]} />
            <Area
              yAxisId="tvl"
              type="monotone"
              dataKey="tvlUSD"
              stroke="#22d3a8"
              strokeWidth={2}
              fill="url(#poolTvlHistory)"
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function PoolDetail({
  pool,
  stats,
  subgraphData,
}: {
  pool: MarketPair;
  stats: PoolStats;
  subgraphData: PairStatsData | undefined;
}) {
  const { pairAddress, baseAddress, reserveBase, reserveQuote, totalSupply, lpBalance, sharePercent } = usePoolData(pool);
  const subgraph = findSubgraphPair(subgraphData, pairAddress);
  const latestDay = subgraph?.days[0];

  const priceBaseInQuote =
    reserveBase && reserveQuote && reserveBase > 0n ? toNum(reserveQuote) / toNum(reserveBase) : undefined;
  const tvl = stats.tvlOf(pool.id);
  const priceBaseUsd = stats.priceOf.get(pool.base);
  const priceQuoteUsd = stats.priceOf.get(pool.quote);

  const rBaseNum = reserveBase !== undefined ? toNum(reserveBase) : undefined;
  const rQuoteNum = reserveQuote !== undefined ? toNum(reserveQuote) : undefined;
  const baseUsdVal = rBaseNum !== undefined && priceBaseUsd !== undefined ? rBaseNum * priceBaseUsd : undefined;
  const quoteUsdVal = rQuoteNum !== undefined && priceQuoteUsd !== undefined ? rQuoteNum * priceQuoteUsd : undefined;
  const basePct =
    baseUsdVal !== undefined && quoteUsdVal !== undefined && baseUsdVal + quoteUsdVal > 0
      ? (baseUsdVal / (baseUsdVal + quoteUsdVal)) * 100
      : 50;

  const volume24hUsd = latestDay ? finiteNumber(latestDay.dailyVolumeUSD) : undefined;
  const estApr = tvl && tvl > 0 && volume24hUsd !== undefined && (pool.base === 'USDT' || pool.quote === 'USDT')
    ? (volume24hUsd * 365 * LP_FEE_PCT) / tvl
    : undefined;

  const myValue =
    lpBalance && totalSupply && totalSupply > 0n && tvl !== undefined
      ? (Number(lpBalance) / Number(totalSupply)) * tvl
      : 0;

  return (
    <div className="rounded-xl border border-abyss-700 bg-abyss-850 p-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="flex -space-x-2">
            <TokenIcon symbol={pool.base} size={32} />
            <TokenIcon symbol={pool.quote} size={32} />
          </div>
          <div>
            <div className="text-base font-semibold text-foam-100">
              {pool.base}/{pool.quote}
            </div>
            <div className="text-xs text-foam-500 tabular-nums">
              1 {pool.base} = {priceBaseInQuote !== undefined ? priceBaseInQuote.toLocaleString('en-US', { maximumFractionDigits: 6 }) : '-'} {pool.quote}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] text-foam-500">예상 수수료 APR</div>
          <div className="text-lg font-semibold text-tide-400 tabular-nums">
            {estApr !== undefined ? `${estApr.toFixed(1)}%` : '-'}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
        <DetailStat icon={Droplets} label="TVL" value={formatPoolTvl(pool, stats)} />
        <DetailStat
          icon={TrendingUp}
          label="거래량 / 24h"
          value={formatVolume24h(pool, subgraph?.pair, latestDay)}
        />
        <DetailStat icon={Layers} label="LP 총 공급량" value={fmt(totalSupply, 2)} />
        <DetailStat
          icon={Activity}
          label="내 포지션"
          value={lpBalance && lpBalance > 0n ? fmtUsd(myValue) : '-'}
          sub={lpBalance && lpBalance > 0n ? `지분 ${sharePercent.toFixed(4)}%` : undefined}
        />
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between text-[11px] mb-1.5">
          <span className="flex items-center gap-1.5 text-foam-300">
            <TrendingUp size={12} className="text-tide-400" />
            가격 추이 (서브그래프)
          </span>
          <span className="text-foam-500">
            풀 시세 {priceBaseInQuote !== undefined ? priceBaseInQuote.toLocaleString('en-US', { maximumFractionDigits: 6 }) : '-'}
          </span>
        </div>
        {subgraph?.pair && subgraph.days.length > 0 && baseAddress ? (
          <PoolSparkline pool={pool} pair={subgraph.pair} days={subgraph.days} baseAddress={baseAddress} />
        ) : (
          <div className="h-20 flex items-center justify-center text-xs text-foam-600">서브그래프 데이터 없음</div>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-abyss-700">
        <div className="flex items-center justify-between text-[11px] mb-1.5">
          <span className="flex items-center gap-1.5 text-foam-300">
            <Droplets size={12} className="text-sky-400" />
            거래량 · TVL 추이 (서브그래프)
          </span>
          <span className="text-foam-500">최근 최대 90일</span>
        </div>
        {subgraph?.days.length ? (
          <PoolLiquidityHistory days={subgraph.days} />
        ) : (
          <div className="h-36 flex items-center justify-center text-xs text-foam-600">서브그래프 데이터 없음</div>
        )}
      </div>

      <div className="mt-4">
        <div className="flex justify-between text-[11px] mb-1.5">
          <span className="text-tide-400 font-medium tabular-nums">
            {fmt(reserveBase, 2)} {pool.base}
          </span>
          <span className="text-sky-400 font-medium tabular-nums">
            {fmt(reserveQuote, 2)} {pool.quote}
          </span>
        </div>
        <div className="h-2.5 rounded-full bg-abyss-900 overflow-hidden flex">
          <div className="h-full bg-tide-500 transition-all duration-500" style={{ width: `${basePct}%` }} />
          <div className="h-full bg-sky-500 transition-all duration-500" style={{ width: `${100 - basePct}%` }} />
        </div>
        <div className="flex justify-between text-[10px] text-foam-600 mt-1">
          <span>{baseUsdVal !== undefined ? fmtUsd(baseUsdVal) : '—'}</span>
          <span>{quoteUsdVal !== undefined ? fmtUsd(quoteUsdVal) : '—'}</span>
        </div>
      </div>
    </div>
  );
}

function DetailStat({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: typeof Droplets;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="p-3 rounded-lg bg-abyss-900/60 border border-abyss-700">
      <div className="flex items-center gap-1.5 text-[11px] text-foam-500">
        <Icon size={12} />
        {label}
      </div>
      <div className="text-sm font-semibold text-foam-100 mt-1 tabular-nums truncate">{value}</div>
      {sub && <div className="text-[10px] text-tide-400 mt-0.5 tabular-nums">{sub}</div>}
    </div>
  );
}

function usePoolData(pool: MarketPair) {
  const { address, chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const pairAddress = getPairAddress(pool.id, readChainId);
  const baseAddress = getTokenAddress(pool.base, readChainId);

  const { data: reserves } = useReadContract({
    address: pairAddress,
    abi: PAIR_ABI,
    functionName: 'getReserves',
    chainId: readChainId,
    query: { enabled: Boolean(pairAddress), refetchInterval: 6000 },
  });

  const { data: token0 } = useReadContract({
    address: pairAddress,
    abi: PAIR_ABI,
    functionName: 'token0',
    chainId: readChainId,
    query: { enabled: Boolean(pairAddress) },
  });

  const { data: totalSupply } = useReadContract({
    address: pairAddress,
    abi: PAIR_ABI,
    functionName: 'totalSupply',
    chainId: readChainId,
    query: { enabled: Boolean(pairAddress), refetchInterval: 6000 },
  });

  const { data: lpBalance, refetch: refetchLpBalance } = useReadContract({
    address: pairAddress,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(pairAddress && address) },
  });

  const baseIsToken0 = token0 && baseAddress ? token0.toLowerCase() === baseAddress.toLowerCase() : undefined;
  const reserveBase = reserves && baseIsToken0 !== undefined ? (baseIsToken0 ? reserves[0] : reserves[1]) : undefined;
  const reserveQuote = reserves && baseIsToken0 !== undefined ? (baseIsToken0 ? reserves[1] : reserves[0]) : undefined;

  const sharePercent =
    lpBalance && totalSupply && totalSupply > 0n ? (Number(lpBalance) / Number(totalSupply)) * 100 : 0;

  return {
    readChainId,
    pairAddress,
    baseAddress,
    reserveBase,
    reserveQuote,
    totalSupply,
    lpBalance,
    refetchLpBalance,
    sharePercent,
  };
}

function PoolRow({
  pool,
  stats,
  subgraphData,
  isSelected,
  onSelect,
}: {
  pool: MarketPair;
  stats: PoolStats;
  subgraphData: PairStatsData | undefined;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const { pairAddress, reserveBase, reserveQuote, lpBalance, sharePercent } = usePoolData(pool);
  const subgraph = findSubgraphPair(subgraphData, pairAddress);
  const latestDay = subgraph?.days[0];

  const priceBaseUsd = stats.priceOf.get(pool.base);
  const priceQuoteUsd = stats.priceOf.get(pool.quote);
  const baseVal = reserveBase !== undefined && priceBaseUsd !== undefined ? toNum(reserveBase) * priceBaseUsd : undefined;
  const quoteVal =
    reserveQuote !== undefined && priceQuoteUsd !== undefined ? toNum(reserveQuote) * priceQuoteUsd : undefined;
  const basePct =
    baseVal !== undefined && quoteVal !== undefined && baseVal + quoteVal > 0
      ? (baseVal / (baseVal + quoteVal)) * 100
      : 50;

  return (
    <button
      onClick={onSelect}
      className={`w-full text-left p-4 transition-colors ${isSelected ? 'bg-tide-400/10' : 'hover:bg-abyss-800/60'}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex -space-x-1.5">
            <TokenIcon symbol={pool.base} size={20} />
            <TokenIcon symbol={pool.quote} size={20} />
          </div>
          <span className="text-sm font-medium text-foam-100 truncate">
            {pool.base}/{pool.quote}
          </span>
        </div>
        <div className="flex items-start gap-3 text-right shrink-0">
          <div>
            <div className="text-[10px] text-foam-500">24h</div>
            <div className="text-xs font-semibold text-foam-300 tabular-nums">
              {formatVolume24h(pool, subgraph?.pair, latestDay)}
            </div>
          </div>
          <div>
            <div className="text-[10px] text-foam-500">TVL</div>
            <div className="text-xs font-semibold text-foam-100 tabular-nums">{formatPoolTvl(pool, stats)}</div>
          </div>
        </div>
      </div>

      <div className="h-1.5 rounded-full bg-abyss-900 overflow-hidden flex mb-2">
        <div className="h-full bg-tide-500/80" style={{ width: `${basePct}%` }} />
        <div className="h-full bg-sky-500/80" style={{ width: `${100 - basePct}%` }} />
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-foam-500">
        <span>
          {fmt(reserveBase, 2)} {pool.base}
        </span>
        <span className="text-right">
          {fmt(reserveQuote, 2)} {pool.quote}
        </span>
        {lpBalance !== undefined && lpBalance > 0n && (
          <span className="col-span-2 text-tide-400">내 지분: {sharePercent.toFixed(4)}%</span>
        )}
      </div>
    </button>
  );
}

function AddLiquidity({ pool }: { pool: MarketPair }) {
  const { address, isConnected, chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const onCorrectChain = chainId === TARGET_CHAIN.id;
  const deployment = getDeployment(readChainId);

  const { reserveBase, reserveQuote, refetchLpBalance } = usePoolData(pool);
  const hasLiquidity = Boolean(reserveBase && reserveQuote && reserveBase > 0n && reserveQuote > 0n);

  const [amountBase, setAmountBase] = useState('');
  const [amountQuote, setAmountQuote] = useState('');
  const [txError, setTxError] = useState<string | null>(null);

  const isBaseNative = pool.base === 'BNB';
  const isQuoteNative = pool.quote === 'BNB';
  const baseAddress = getTokenAddress(pool.base, readChainId);
  const quoteAddress = getTokenAddress(pool.quote, readChainId);

  const updateBase = (value: string) => {
    setAmountBase(value);
    if (hasLiquidity && reserveBase && reserveQuote) {
      const parsed = safeParse(value);
      if (parsed) {
        const quote = (parsed * reserveQuote) / reserveBase;
        setAmountQuote(formatUnits(quote, DECIMALS));
      } else {
        setAmountQuote('');
      }
    }
  };

  const updateQuote = (value: string) => {
    setAmountQuote(value);
    if (hasLiquidity && reserveBase && reserveQuote) {
      const parsed = safeParse(value);
      if (parsed) {
        const base = (parsed * reserveBase) / reserveQuote;
        setAmountBase(formatUnits(base, DECIMALS));
      } else {
        setAmountBase('');
      }
    }
  };

  const amountBaseWei = safeParse(amountBase);
  const amountQuoteWei = safeParse(amountQuote);

  const { data: allowanceBase, refetch: refetchAllowanceBase } = useReadContract({
    address: baseAddress,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: address && deployment ? [address, deployment.router] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(address && deployment && baseAddress && !isBaseNative) },
  });

  const { data: allowanceQuote, refetch: refetchAllowanceQuote } = useReadContract({
    address: quoteAddress,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: address && deployment ? [address, deployment.router] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(address && deployment && quoteAddress && !isQuoteNative) },
  });

  const needsBaseApproval = !isBaseNative && amountBaseWei !== null && (allowanceBase === undefined || allowanceBase < amountBaseWei);
  const needsQuoteApproval = !isQuoteNative && amountQuoteWei !== null && (allowanceQuote === undefined || allowanceQuote < amountQuoteWei);

  const { writeContract: writeApprove, data: approveHash, isPending: isApprovePending } = useWriteContract();
  const { isLoading: isApproveConfirming, isSuccess: isApproveConfirmed } = useWaitForTransactionReceipt({ hash: approveHash });

  const { writeContract: writeSupply, data: supplyHash, isPending: isSupplyPending, reset: resetSupply } = useWriteContract();
  const { isLoading: isSupplyConfirming, isSuccess: isSupplyConfirmed } = useWaitForTransactionReceipt({ hash: supplyHash });

  useEffect(() => {
    if (isApproveConfirmed) {
      refetchAllowanceBase();
      refetchAllowanceQuote();
    }
  }, [isApproveConfirmed, refetchAllowanceBase, refetchAllowanceQuote]);

  useEffect(() => {
    if (isSupplyConfirmed && supplyHash && address) {
      logTransaction({
        type: 'addLiquidity',
        hash: supplyHash,
        account: address,
        chainId: readChainId,
        details: { pool: `${pool.base}-${pool.quote}`, amountBase, amountQuote },
      });
      setAmountBase('');
      setAmountQuote('');
      refetchLpBalance();
      resetSupply();
    }
  }, [address, amountBase, amountQuote, isSupplyConfirmed, pool.base, pool.quote, readChainId, refetchLpBalance, resetSupply, supplyHash]);

  const approveToken = (tokenAddress: `0x${string}` | undefined) => {
    if (!tokenAddress || !deployment) return;
    setTxError(null);
    writeApprove(
      { address: tokenAddress, abi: ERC20_ABI, functionName: 'approve', args: [deployment.router, 2n ** 256n - 1n] },
      { onError: (err) => setTxError(err.message) },
    );
  };

  const handleSupply = () => {
    if (!amountBaseWei || !amountQuoteWei || !deployment || !address || !baseAddress || !quoteAddress) return;
    setTxError(null);
    const minBase = (amountBaseWei * BigInt(Math.round((100 - SLIPPAGE_PERCENT) * 100))) / 10000n;
    const minQuote = (amountQuoteWei * BigInt(Math.round((100 - SLIPPAGE_PERCENT) * 100))) / 10000n;
    const deadline = BigInt(Math.floor(Date.now() / 1000) + DEADLINE_SECONDS);

    if (isBaseNative || isQuoteNative) {
      const tokenAddr = isBaseNative ? quoteAddress : baseAddress;
      const tokenAmount = isBaseNative ? amountQuoteWei : amountBaseWei;
      const tokenMin = isBaseNative ? minQuote : minBase;
      const bnbAmount = isBaseNative ? amountBaseWei : amountQuoteWei;
      const bnbMin = isBaseNative ? minBase : minQuote;
      writeSupply(
        {
          address: deployment.router,
          abi: ROUTER_ABI,
          functionName: 'addLiquidityBNB',
          args: [tokenAddr, tokenAmount, tokenMin, bnbMin, address, deadline],
          value: bnbAmount,
        },
        { onError: (err) => setTxError(err.message) },
      );
    } else {
      writeSupply(
        {
          address: deployment.router,
          abi: ROUTER_ABI,
          functionName: 'addLiquidity',
          args: [baseAddress, quoteAddress, amountBaseWei, amountQuoteWei, minBase, minQuote, address, deadline],
        },
        { onError: (err) => setTxError(err.message) },
      );
    }
  };

  const isPending = isApprovePending || isApproveConfirming || isSupplyPending || isSupplyConfirming;

  let buttonLabel = 'Enter an amount';
  let buttonAction: (() => void) | undefined;
  let buttonDisabled = true;

  if (!isConnected) {
    buttonLabel = 'Connect wallet to add liquidity';
  } else if (!onCorrectChain) {
    buttonLabel = 'Switch network';
  } else if (amountBaseWei && amountQuoteWei) {
    if (needsBaseApproval) {
      buttonLabel = `Approve ${pool.base}`;
      buttonAction = () => approveToken(baseAddress);
      buttonDisabled = isPending;
    } else if (needsQuoteApproval) {
      buttonLabel = `Approve ${pool.quote}`;
      buttonAction = () => approveToken(quoteAddress);
      buttonDisabled = isPending;
    } else {
      buttonLabel = 'Supply';
      buttonAction = handleSupply;
      buttonDisabled = isPending;
    }
  }

  return (
    <div className="space-y-3">
      {!hasLiquidity && (
        <div className="p-3 rounded-lg border border-tide-400/30 bg-tide-400/5 text-xs text-tide-400">
          This pool has no liquidity yet — you'll set the initial price.
        </div>
      )}
      <AmountInput label={pool.base} amount={amountBase} onChange={updateBase} />
      <div className="flex justify-center text-foam-500">+</div>
      <AmountInput label={pool.quote} amount={amountQuote} onChange={updateQuote} />

      <button
        onClick={buttonAction}
        disabled={buttonDisabled}
        className="w-full mt-2 py-3 rounded-xl font-semibold text-sm transition-colors disabled:bg-abyss-700 disabled:text-foam-500 disabled:cursor-not-allowed bg-tide-500 text-abyss-950 hover:bg-tide-400"
      >
        {buttonLabel}
      </button>

      {txError && <div className="px-3 py-2 rounded-md bg-coral-500/15 text-coral-400 text-xs break-words">{txError}</div>}
      {isSupplyConfirmed && !txError && (
        <div className="px-3 py-2 rounded-md bg-tide-400/10 text-tide-400 text-xs">Liquidity added on-chain.</div>
      )}
    </div>
  );
}

function RemoveLiquidity({ pool }: { pool: MarketPair }) {
  const { address, isConnected, chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const onCorrectChain = chainId === TARGET_CHAIN.id;
  const deployment = getDeployment(readChainId);

  const { pairAddress, reserveBase, reserveQuote, totalSupply, lpBalance, refetchLpBalance } = usePoolData(pool);

  const [percent, setPercent] = useState(0);
  const [txError, setTxError] = useState<string | null>(null);

  const liquidityWei = lpBalance ? (lpBalance * BigInt(percent)) / 100n : 0n;

  const expectedBase =
    liquidityWei && totalSupply && totalSupply > 0n && reserveBase ? (liquidityWei * reserveBase) / totalSupply : 0n;
  const expectedQuote =
    liquidityWei && totalSupply && totalSupply > 0n && reserveQuote ? (liquidityWei * reserveQuote) / totalSupply : 0n;

  const { data: lpAllowance, refetch: refetchLpAllowance } = useReadContract({
    address: pairAddress,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: address && deployment ? [address, deployment.router] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(address && deployment && pairAddress) },
  });

  const needsApproval = liquidityWei > 0n && (lpAllowance === undefined || lpAllowance < liquidityWei);

  const { writeContract: writeApprove, data: approveHash, isPending: isApprovePending } = useWriteContract();
  const { isLoading: isApproveConfirming, isSuccess: isApproveConfirmed } = useWaitForTransactionReceipt({ hash: approveHash });

  const { writeContract: writeRemove, data: removeHash, isPending: isRemovePending, reset: resetRemove } = useWriteContract();
  const { isLoading: isRemoveConfirming, isSuccess: isRemoveConfirmed } = useWaitForTransactionReceipt({ hash: removeHash });

  useEffect(() => {
    if (isApproveConfirmed) refetchLpAllowance();
  }, [isApproveConfirmed, refetchLpAllowance]);

  useEffect(() => {
    if (isRemoveConfirmed && removeHash && address) {
      logTransaction({
        type: 'removeLiquidity',
        hash: removeHash,
        account: address,
        chainId: readChainId,
        details: { pool: `${pool.base}-${pool.quote}`, percent: String(percent) },
      });
      setPercent(0);
      refetchLpBalance();
      resetRemove();
    }
  }, [address, isRemoveConfirmed, percent, pool.base, pool.quote, readChainId, refetchLpBalance, removeHash, resetRemove]);

  const handleApprove = () => {
    if (!pairAddress || !deployment) return;
    setTxError(null);
    writeApprove(
      { address: pairAddress, abi: ERC20_ABI, functionName: 'approve', args: [deployment.router, 2n ** 256n - 1n] },
      { onError: (err) => setTxError(err.message) },
    );
  };

  const handleRemove = () => {
    if (!deployment || !address || liquidityWei <= 0n) return;
    setTxError(null);
    const minBase = (expectedBase * BigInt(Math.round((100 - SLIPPAGE_PERCENT) * 100))) / 10000n;
    const minQuote = (expectedQuote * BigInt(Math.round((100 - SLIPPAGE_PERCENT) * 100))) / 10000n;
    const deadline = BigInt(Math.floor(Date.now() / 1000) + DEADLINE_SECONDS);

    const isBaseNative = pool.base === 'BNB';
    const isQuoteNative = pool.quote === 'BNB';
    const baseAddress = getTokenAddress(pool.base, readChainId);
    const quoteAddress = getTokenAddress(pool.quote, readChainId);

    if (isBaseNative || isQuoteNative) {
      const tokenAddr = isBaseNative ? quoteAddress : baseAddress;
      const tokenMin = isBaseNative ? minQuote : minBase;
      const bnbMin = isBaseNative ? minBase : minQuote;
      if (!tokenAddr) return;
      writeRemove(
        {
          address: deployment.router,
          abi: ROUTER_ABI,
          functionName: 'removeLiquidityBNB',
          args: [tokenAddr, liquidityWei, tokenMin, bnbMin, address, deadline],
        },
        { onError: (err) => setTxError(err.message) },
      );
    } else {
      if (!baseAddress || !quoteAddress) return;
      writeRemove(
        {
          address: deployment.router,
          abi: ROUTER_ABI,
          functionName: 'removeLiquidity',
          args: [baseAddress, quoteAddress, liquidityWei, minBase, minQuote, address, deadline],
        },
        { onError: (err) => setTxError(err.message) },
      );
    }
  };

  const isPending = isApprovePending || isApproveConfirming || isRemovePending || isRemoveConfirming;

  let buttonLabel = 'Select an amount';
  let buttonAction: (() => void) | undefined;
  let buttonDisabled = true;

  if (!isConnected) {
    buttonLabel = 'Connect wallet to remove liquidity';
  } else if (!onCorrectChain) {
    buttonLabel = 'Switch network';
  } else if (!lpBalance || lpBalance === 0n) {
    buttonLabel = 'No LP tokens in this pool';
  } else if (liquidityWei > 0n) {
    if (needsApproval) {
      buttonLabel = 'Approve LP tokens';
      buttonAction = handleApprove;
      buttonDisabled = isPending;
    } else {
      buttonLabel = 'Remove';
      buttonAction = handleRemove;
      buttonDisabled = isPending;
    }
  }

  return (
    <div className="space-y-4">
      <div className="p-4 rounded-xl border border-abyss-700 bg-abyss-850">
        <div className="flex justify-between text-xs text-foam-500 mb-3">
          <span>Your LP balance</span>
          <span>{fmt(lpBalance, 6)}</span>
        </div>
        <div className="text-3xl font-semibold text-foam-100 mb-3">{percent}%</div>
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={percent}
          onChange={(e) => setPercent(Number(e.target.value))}
          className="w-full accent-tide-500"
        />
        <div className="flex gap-2 mt-3">
          {[25, 50, 75, 100].map((p) => (
            <button
              key={p}
              onClick={() => setPercent(p)}
              className="flex-1 py-1.5 rounded-md text-xs font-medium bg-abyss-700 text-foam-300 hover:bg-abyss-600"
            >
              {p}%
            </button>
          ))}
        </div>
      </div>

      {liquidityWei > 0n && (
        <div className="p-3 rounded-lg border border-abyss-700 text-xs text-foam-500 space-y-1">
          <div className="flex justify-between">
            <span>{pool.base}</span>
            <span className="text-foam-300">{fmt(expectedBase, 6)}</span>
          </div>
          <div className="flex justify-between">
            <span>{pool.quote}</span>
            <span className="text-foam-300">{fmt(expectedQuote, 6)}</span>
          </div>
        </div>
      )}

      <button
        onClick={buttonAction}
        disabled={buttonDisabled}
        className="w-full py-3 rounded-xl font-semibold text-sm transition-colors disabled:bg-abyss-700 disabled:text-foam-500 disabled:cursor-not-allowed bg-tide-500 text-abyss-950 hover:bg-tide-400"
      >
        {buttonLabel}
      </button>

      {txError && <div className="px-3 py-2 rounded-md bg-coral-500/15 text-coral-400 text-xs break-words">{txError}</div>}
      {isRemoveConfirmed && !txError && (
        <div className="px-3 py-2 rounded-md bg-tide-400/10 text-tide-400 text-xs">Liquidity removed on-chain.</div>
      )}
    </div>
  );
}

function AmountInput({ label, amount, onChange }: { label: string; amount: string; onChange: (v: string) => void }) {
  return (
    <div className="p-3 rounded-xl border border-abyss-700 bg-abyss-850">
      <div className="flex items-center justify-between text-xs text-foam-500 mb-2">
        <span>{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <input
          value={amount}
          onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ''))}
          placeholder="0.0"
          className="w-full bg-transparent text-2xl font-medium text-foam-100 outline-none placeholder:text-foam-700 tabular-nums"
        />
        <TokenIcon symbol={label} size={22} />
      </div>
    </div>
  );
}
